import { useState, useMemo, useEffect } from "react";
import {
  Settings, X, Check, ArrowRight, ArrowLeft, Lock,
  Flame, Calendar, TrendingUp, FileText, Truck, Sparkles, ChevronRight
} from "lucide-react";
import { C, SERIF, SANS } from "../lib/constants";
import { Btn } from "../components/Primitives";
import CycleCalendar from "../components/CycleCalendar";
import { useAuth } from "../lib/AuthContext";
import { trackerApi, API } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { SEO } from "../lib/seoRoutes";

// ── PHASE SYSTEM ─────────────────────────────────────────────────────────────
const PHASES = {
  menstrual: {
    label: "Menstrual",
    color: "#C5603A", bg: "#F7E8E0", text: "#8A3E22",
    headline: "Menstrual phase — rest is the work.",
    sub: "Energy is naturally at its lowest. This is biology, not laziness.",
    coach: {
      base: "You're in your menstrual phase. Energy and iron levels are naturally low right now — your body is doing real work. Focus on warmth, iron-rich foods, and gentle movement only. Skip anything intense; it will cost more than it gives this week.",
      chips: ["Iron-rich foods", "Gentle movement", "Prioritise warmth"],
    },
    plan: {
      morning:   { icon: "\u2615", text: "Warm water with soaked dates — a gentle start, no rush" },
      afternoon: { icon: "\ud83e\uddd8", text: "Restorative stretching or a short slow walk — nothing intense" },
      evening:   { icon: "\ud83c\udf19", text: "Heat pad on lower belly + early wind-down" },
      nutrition: { icon: "\ud83e\udd6c", text: "Iron-rich: dal, sesame, dates, leafy greens, jaggery" },
    },
  },
  follicular: {
    label: "Follicular",
    color: "#3A6E4E", bg: "#E4EFE8", text: "#25523A",
    headline: "Follicular phase — your energy is returning.",
    sub: "Estrogen is rising. This is your building week.",
    coach: {
      base: "You're in your follicular phase. Estrogen is climbing, which means energy, mood, and focus all trend upward this week. It's the best window in your cycle for strength training, starting new projects, and tackling hard problems.",
      chips: ["Strength training", "Start something new", "Protein-rich meals"],
    },
    plan: {
      morning:   { icon: "\ud83c\udf05", text: "10-minute movement + protein-rich breakfast" },
      afternoon: { icon: "\ud83d\udcaa", text: "Great window for strength training or a demanding task" },
      evening:   { icon: "\ud83d\udcdd", text: "Plan the week ahead — your brain is sharpest now" },
      nutrition: { icon: "\ud83e\udd5a", text: "Fresh + protein: sprouts, paneer, eggs, seasonal fruit" },
    },
  },
  ovulatory: {
    label: "Ovulatory",
    color: "#BA8A2D", bg: "#F7EDDA", text: "#7A5A18",
    headline: "Ovulatory phase — you're at your peak.",
    sub: "Energy, confidence, and communication are at their highest.",
    coach: {
      base: "You're in your ovulatory phase — the energetic peak of your cycle. High-intensity workouts, important conversations, and social plans all land better in this window. If there's something big you've been putting off, this is the week.",
      chips: ["Peak workouts OK", "Key conversations", "Antioxidants + fibre"],
    },
    plan: {
      morning:   { icon: "\u26a1", text: "High-energy workout if you feel like it — HIIT is fine now" },
      afternoon: { icon: "\ud83d\udde3", text: "Schedule important conversations — communication peaks" },
      evening:   { icon: "\u2728", text: "Social plans land well this week — say yes" },
      nutrition: { icon: "\ud83e\uded0", text: "Antioxidants + fibre: berries, nuts, whole grains" },
    },
  },
  luteal: {
    label: "Luteal",
    color: "#7C6B9E", bg: "#EDE9F4", text: "#4E3F70",
    headline: "Luteal phase — soften the pace.",
    sub: "Progesterone is rising. Your body is preparing, not slacking.",
    coach: {
      base: "You're in your luteal phase. Progesterone is rising, which naturally lowers energy and can heighten emotions in the final days. Swap intense workouts for yoga or walking, favour complex carbs and magnesium, and protect your sleep. Slowing down now is strategy.",
      chips: ["Yoga over HIIT", "Complex carbs + B6", "Protect your sleep"],
    },
    plan: {
      morning:   { icon: "\ud83c\udf75", text: "Slower start — warm breakfast, avoid caffeine on empty stomach" },
      afternoon: { icon: "\ud83d\udeb6", text: "Swap HIIT for yoga or a walk — recovery costs more now" },
      evening:   { icon: "\ud83c\udf19", text: "Magnesium-rich snack + wind-down; aim for 8 hours" },
      nutrition: { icon: "\ud83c\udf60", text: "Complex carbs: sweet potato, banana, whole grains, dark chocolate" },
    },
  },
};

function getPhase(day, periodLen, cycleLen) {
  const ovulationDay = cycleLen - 14;
  if (day <= periodLen) return "menstrual";
  if (day < ovulationDay - 1) return "follicular";
  if (day <= ovulationDay + 1) return "ovulatory";
  return "luteal";
}

function Pill({ label, active, onClick, color = C.coral, bgActive = C.coralBg }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 18px", borderRadius: 22, cursor: "pointer",
      border: `1.5px solid ${active ? color : C.border}`,
      background: active ? bgActive : C.white,
      fontFamily: SANS, fontSize: 13,
      color: active ? color : C.muted,
      fontWeight: active ? 700 : 400,
      transition: "all 0.12s",
    }}>{label}</button>
  );
}

function SectionLabel({ children }) {
  return <div style={{
    fontFamily: SANS, fontSize: 10, letterSpacing: 2.5,
    color: C.muted, textTransform: "uppercase", fontWeight: 600, marginBottom: 10,
  }}>{children}</div>;
}

// ── ONBOARDING ───────────────────────────────────────────────────────────────
// Local-time ISO date (YYYY-MM-DD). Prefer over toISOString() so an early-morning
// IST log doesn't roll back to the previous UTC day.
function toLocalIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toLocalIso(d);
}

// Format an ISO date (YYYY-MM-DD) as a short "Mon D" label in local time,
// safely (no UTC drift).
function formatShortDay(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function Onboarding({ onComplete, onSkip, user, onSignIn }) {
  const [step, setStep] = useState(0);
  const [lastPeriod, setLastPeriod] = useState(() => isoDaysAgo(7));
  const [cycleLen, setCycleLen] = useState(28);
  const [periodLen, setPeriodLen] = useState(5);

  const Stepper = ({ val, set, min, max, unit }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 20, justifyContent: "center" }}>
      <button onClick={() => set(Math.max(min, val - 1))} style={{
        width: 48, height: 48, borderRadius: "50%", border: `1px solid ${C.border}`,
        background: C.white, cursor: "pointer", fontSize: 20, color: C.dark,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>−</button>
      <div style={{ textAlign: "center", minWidth: 90 }}>
        <div style={{ fontFamily: SERIF, fontSize: 52, color: C.dark, lineHeight: 1 }}>{val}</div>
        <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, marginTop: 4 }}>{unit}</div>
      </div>
      <button onClick={() => set(Math.min(max, val + 1))} style={{
        width: 48, height: 48, borderRadius: "50%", border: `1px solid ${C.border}`,
        background: C.white, cursor: "pointer", fontSize: 20, color: C.dark,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>+</button>
    </div>
  );

  const steps = [
    {
      title: "Let's get to know your cycle.",
      body: "Three gentle questions — about 30 seconds. Everything you share is encrypted, never sold, and yours to delete anytime.",
      content: (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          background: C.greenBg, borderRadius: 10, padding: "14px 18px", marginTop: 8,
        }}>
          <Lock size={16} color={C.green} />
          <span style={{ fontFamily: SANS, fontSize: 13, color: C.green, lineHeight: 1.5 }}>
            Your cycle data stays on your account. We never share it. Full stop.
          </span>
        </div>
      ),
      cta: "Let's begin",
    },
    {
      title: "When did your last period start?",
      body: "Your best guess is fine — you can always adjust it later.",
      content: (
        <input type="date" value={lastPeriod} onChange={e => setLastPeriod(e.target.value)}
          style={{
            width: "100%", padding: "16px 18px", border: `1.5px solid ${C.border}`,
            borderRadius: 10, fontFamily: SANS, fontSize: 16, color: C.dark,
            background: C.white, outline: "none", boxSizing: "border-box",
          }} />
      ),
      cta: "Next",
    },
    {
      title: "How long is your typical cycle?",
      body: "From the first day of one period to the first day of the next. Not sure? 28 is a common starting point — we'll learn your real rhythm as you track.",
      content: <Stepper val={cycleLen} set={setCycleLen} min={21} max={45} unit="days" />,
      cta: "Next",
    },
    {
      title: "And how many days does your period usually last?",
      body: "This helps us know which phase you're in each day.",
      content: <Stepper val={periodLen} set={setPeriodLen} min={2} max={10} unit="days" />,
      cta: "Open my tracker",
    },
  ];

  const s = steps[step];
  return (
    <div style={{
      background: C.bg, display: "flex",
      alignItems: "center", justifyContent: "center", padding: "56px 24px 72px",
    }}>
      <div style={{ maxWidth: 480, width: "100%" }}>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 32 }}>
          {steps.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 24 : 8, height: 8, borderRadius: 4,
              background: i <= step ? C.coral : C.border, transition: "all 0.25s",
            }} />
          ))}
        </div>
        <div style={{
          background: C.white, borderRadius: 18, padding: "36px 32px",
          border: `1px solid ${C.border}`, textAlign: "center",
        }}>
          <h1 style={{
            fontFamily: SERIF, fontSize: 30, color: C.dark, fontWeight: 400,
            margin: "0 0 12px", lineHeight: 1.25,
          }}>{s.title}</h1>
          <p style={{
            fontFamily: SANS, fontSize: 14, color: C.muted, lineHeight: 1.75,
            margin: "0 0 28px",
          }}>{s.body}</p>
          <div style={{ marginBottom: 32 }}>{s.content}</div>
          <Btn full size="lg" onClick={() => {
            if (step < steps.length - 1) setStep(step + 1);
            else onComplete({ lastPeriod, cycleLen, periodLen });
          }}>
            {s.cta} <ArrowRight size={15} />
          </Btn>
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: SANS, fontSize: 13, color: C.muted, marginTop: 14,
              display: "inline-flex", alignItems: "center", gap: 5,
            }}>
              <ArrowLeft size={13} /> Back
            </button>
          )}
        </div>
        <div style={{ textAlign: "center", marginTop: 18 }}>
          <button onClick={onSkip} style={{
            background: "none", border: "none", cursor: "pointer",
            fontFamily: SANS, fontSize: 13, color: C.mutedLt, textDecoration: "underline",
          }}>Skip — explore with sample data</button>
        </div>
      </div>
    </div>
  );
}

// ── SETTINGS MODAL ───────────────────────────────────────────────────────────
function SettingsModal({ open, onClose, settings, onSave }) {
  const [lastPeriod, setLastPeriod] = useState(settings.lastPeriod);
  const [cycleLen, setCycleLen] = useState(settings.cycleLen);
  const [periodLen, setPeriodLen] = useState(settings.periodLen);
  if (!open) return null;

  const Row = ({ label, children }) => (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );
  const NumInput = ({ val, set, min, max }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <button onClick={() => set(Math.max(min, val - 1))} style={{
        width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`,
        background: C.white, cursor: "pointer", fontSize: 16, color: C.dark,
      }}>−</button>
      <span style={{ fontFamily: SANS, fontSize: 18, fontWeight: 700, color: C.dark, minWidth: 32, textAlign: "center" }}>{val}</span>
      <button onClick={() => set(Math.min(max, val + 1))} style={{
        width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`,
        background: C.white, cursor: "pointer", fontSize: 16, color: C.dark,
      }}>+</button>
      <span style={{ fontFamily: SANS, fontSize: 12, color: C.muted }}>days</span>
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(26,23,20,0.35)", zIndex: 299 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)", zIndex: 300, background: C.white,
        borderRadius: 18, padding: "28px 30px", width: 420, maxWidth: "92vw",
        boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 24, color: C.dark, fontWeight: 400, margin: 0 }}>Cycle settings</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        <p style={{ fontFamily: SANS, fontSize: 13, color: C.muted, margin: "0 0 24px", lineHeight: 1.6 }}>
          You only need these once — we handle the rest as you track.
        </p>
        <Row label="Last period start date">
          <input type="date" value={lastPeriod} onChange={e => setLastPeriod(e.target.value)}
            style={{
              width: "100%", padding: "12px 14px", border: `1.5px solid ${C.border}`,
              borderRadius: 8, fontFamily: SANS, fontSize: 14, color: C.dark,
              background: C.bg, outline: "none", boxSizing: "border-box",
            }} />
        </Row>
        <Row label="Average cycle length">
          <NumInput val={cycleLen} set={setCycleLen} min={21} max={45} />
        </Row>
        <Row label="Average period length">
          <NumInput val={periodLen} set={setPeriodLen} min={2} max={10} />
        </Row>
        <Btn full size="lg" onClick={() => { onSave({ lastPeriod, cycleLen, periodLen }); onClose(); }}>
          Save settings
        </Btn>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, justifyContent: "center" }}>
          <Lock size={12} color={C.mutedLt} />
          <span style={{ fontFamily: SANS, fontSize: 11, color: C.mutedLt }}>
            Encrypted · Export or delete anytime
          </span>
        </div>
      </div>
    </>
  );
}

// ── CYCLE BAR ────────────────────────────────────────────────────────────────
function CycleBar({ currentDay, cycleLen, periodLen }) {
  const ovulationDay = cycleLen - 14;
  const segments = [
    { key: "menstrual", from: 1, to: periodLen },
    { key: "follicular", from: periodLen + 1, to: ovulationDay - 2 },
    { key: "ovulatory", from: ovulationDay - 1, to: ovulationDay + 1 },
    { key: "luteal", from: ovulationDay + 2, to: cycleLen },
  ];
  const pct = d => ((d - 1) / cycleLen) * 100;
  return (
    <div>
      <div style={{ position: "relative", height: 14, borderRadius: 8, overflow: "hidden", display: "flex", marginBottom: 6 }}>
        {segments.map(seg => {
          const w = ((seg.to - seg.from + 1) / cycleLen) * 100;
          return <div key={seg.key} style={{ width: `${w}%`, height: "100%", background: PHASES[seg.key].bg }} />;
        })}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: `${(currentDay / cycleLen) * 100}%`, background: "rgba(26,23,20,0.06)",
        }} />
        <div style={{
          position: "absolute", top: -2, bottom: -2,
          left: `calc(${pct(currentDay) + (0.5 / cycleLen) * 100}% - 2px)`,
          width: 4, borderRadius: 2, background: C.dark,
        }} />
      </div>
      <div style={{ display: "flex" }}>
        {segments.map(seg => {
          const w = ((seg.to - seg.from + 1) / cycleLen) * 100;
          const active = currentDay >= seg.from && currentDay <= seg.to;
          return (
            <div key={seg.key} style={{ width: `${w}%`, textAlign: "center" }}>
              <span style={{
                fontFamily: SANS, fontSize: 10,
                color: active ? PHASES[seg.key].text : C.mutedLt,
                fontWeight: active ? 700 : 400, letterSpacing: 0.5,
              }}>{PHASES[seg.key].label}</span>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ fontFamily: SANS, fontSize: 10, color: C.mutedLt }}>Day 1</span>
        <span style={{ fontFamily: SANS, fontSize: 10, color: C.mutedLt }}>Day {cycleLen}</span>
      </div>
    </div>
  );
}

// ── MAIN TRACKER ─────────────────────────────────────────────────────────────
// Parse an ISO YYYY-MM-DD string as local midnight (avoids UTC off-by-one).
function parseLocalDate(iso) {
  if (!iso) return new Date();
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function Tracker({ settings, onOpenSettings, user, onSignIn }) {
  const today = startOfDay(new Date());
  const last = startOfDay(parseLocalDate(settings.lastPeriod));
  const diffDays = Math.max(0, Math.floor((today - last) / (1000 * 60 * 60 * 24)));
  const currentDay = (diffDays % settings.cycleLen) + 1;
  const phaseKey = getPhase(currentDay, settings.periodLen, settings.cycleLen);
  const phase = PHASES[phaseKey];
  const daysToNext = settings.cycleLen - currentDay + 1;
  // Compute the upcoming period start based on today (handles cases where
  // last period is many cycles ago).
  const nextPeriod = new Date(today); nextPeriod.setDate(today.getDate() + daysToNext);
  const delivery = new Date(nextPeriod); delivery.setDate(nextPeriod.getDate() - 2);
  const fmt = d => d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });

  const [flow, setFlow] = useState(null);
  const [mood, setMood] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [sleep, setSleep] = useState(null);
  const [move, setMove] = useState(null);
  const [craving, setCraving] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [crampLevel, setCrampLevel] = useState(0);
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [streak, setStreak] = useState(0);
  const [showDoctor, setShowDoctor] = useState(false);
  const [aiCoach, setAiCoach] = useState(null);
  const [logs, setLogs] = useState([]);

  // Load logs for calendar (logged in only)
  useEffect(() => {
    (async () => {
      if (user) {
        try {
          const { data } = await trackerApi.getLogs();
          setLogs(data.logs || []);
        } catch {}
      }
    })();
  }, [user]);

  // Load streak from backend if logged in
  useEffect(() => {
    (async () => {
      if (user) {
        try {
          const { data } = await trackerApi.getStreak();
          setStreak(data.streak || 0);
        } catch {}
      } else {
        setStreak(6); // sample
      }
    })();
  }, [user]);

  const toggleSymptom = s => setSymptoms(prev =>
    prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const handleSave = async () => {
    if (saved || saving) return;
    setSaving(true);
    // Local date (YYYY-MM-DD). Using toISOString would use UTC and shift the
    // date backwards for early-morning IST entries.
    const isoDate = toLocalIso(new Date());
    const logPayload = {
      date: isoDate, day: currentDay, flow, mood, energy, sleep, move, craving,
      symptoms, crampLevel, notes,
    };
    if (user) {
      try {
        const { data } = await trackerApi.putLog(logPayload);
        setStreak(data.streak || (streak + 1));
      } catch {}
    } else {
      setStreak(streak + 1);
    }
    setSaved(true);
    setSaving(false);
    // Fetch AI coach with the logged data
    fetchCoach({ withLog: true });
  };

  const fetchCoach = async (opts = {}) => {
    try {
      const payload = {
        phase: phaseKey, day: currentDay,
        flow: opts.withLog ? flow : null,
        mood: opts.withLog ? mood : null,
        energy: opts.withLog ? energy : null,
        symptoms: opts.withLog ? symptoms : [],
      };
      const { data } = await trackerApi.coach(payload);
      setAiCoach(data);
    } catch {}
  };

  // Fetch initial AI coach message once
  useEffect(() => {
    fetchCoach({ withLog: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseKey]);

  const coachMessage = useMemo(() => {
    if (aiCoach && aiCoach.text) {
      return { qualified: aiCoach.qualified || "Based on your cycle phase", text: aiCoach.text };
    }
    if (saved && (flow || energy || mood)) {
      const parts = [];
      if (flow) parts.push(`${flow.toLowerCase()} flow`);
      if (energy) parts.push(`${energy.toLowerCase()} energy`);
      if (mood) parts.push(`feeling ${mood.toLowerCase()}`);
      const summary = parts.join(", ");
      return { qualified: "Based on what you logged today", text: `You logged ${summary} on Day ${currentDay}.` };
    }
    return { qualified: "Based on your cycle phase", text: phase.coach.base };
  }, [aiCoach, saved, flow, energy, mood, currentDay, phase]);

  const card = { background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: 26 };

  return (
    <div style={{ maxWidth: 1120, margin: "0 auto", padding: "28px 24px 60px" }}>
      {/* Toolbar (streak + settings) */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          background: C.coralBg, borderRadius: 20, padding: "6px 14px",
        }}>
          <Flame size={13} color={C.coral} />
          <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: 700, color: C.coral }}>
            {streak}-day streak
          </span>
        </div>
        <button onClick={onOpenSettings} style={{
          background: "none", border: `1px solid ${C.border}`, borderRadius: "50%",
          width: 36, height: 36, cursor: "pointer", color: C.muted,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Settings size={16} />
        </button>
      </div>

      {/* PHASE HERO */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <span style={{
            background: phase.bg, color: phase.text, fontFamily: SANS,
            fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 14,
            letterSpacing: 0.5,
          }}>Day {currentDay} · {phase.label} phase</span>
          <span style={{ fontFamily: SANS, fontSize: 12, color: C.muted }}>
            {settings.cycleLen}-day cycle
          </span>
        </div>
        <h1 style={{ fontFamily: SERIF, fontSize: 44, color: C.dark, fontWeight: 400, margin: "0 0 8px", lineHeight: 1.1 }}>
          {phase.headline}
        </h1>
        <p style={{ fontFamily: SANS, fontSize: 15, color: C.muted, margin: 0, lineHeight: 1.6 }}>
          {phase.sub}
        </p>
      </div>

      {/* CYCLE BAR + DELIVERY */}
      <div style={{ ...card, marginBottom: 20 }}>
        <CycleBar currentDay={currentDay} cycleLen={settings.cycleLen} periodLen={settings.periodLen} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 20 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", background: C.bg, borderRadius: 10, padding: "14px 16px" }}>
            <Calendar size={17} color={C.coral} />
            <div>
              <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark }}>
                Next period in {daysToNext} days
              </div>
              <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>
                Expected {fmt(nextPeriod)} · prediction improves as you track
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", background: C.greenBg, borderRadius: 10, padding: "14px 16px" }}>
            <Truck size={17} color={C.green} />
            <div>
              <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.green }}>
                Comfort kit ships {fmt(delivery)}
              </div>
              <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>
                Synced to your cycle — 2 days before your period
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MONTHLY CALENDAR */}
      <div style={{ marginBottom: 20 }}>
        <CycleCalendar settings={settings} logs={logs} />
      </div>

      {/* TWO COLUMN */}
      <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 20 }}>
        {/* LOG PANEL */}
        <div style={card}>
          <SectionLabel>Log today · {fmt(today)}</SectionLabel>
          <h2 style={{ fontFamily: SERIF, fontSize: 30, color: C.dark, fontWeight: 400, margin: "0 0 24px" }}>
            How are you, really?
          </h2>
          {[
            { label: "Flow", icon: "○", opts: ["None", "Light", "Medium", "Heavy"], val: flow, set: setFlow },
            { label: "Mood", icon: "♡", opts: ["Calm", "Content", "Tender", "Irritable", "Anxious", "Foggy", "Fierce"], val: mood, set: setMood },
            { label: "Energy", icon: "◎", opts: ["Low", "Steady", "High"], val: energy, set: setEnergy },
            { label: "Sleep", icon: "☽", opts: ["< 5h", "6–7h", "8h+"], val: sleep, set: setSleep },
            { label: "Movement", icon: "◉", opts: ["Rest", "Walk", "Yoga", "Workout"], val: move, set: setMove },
            { label: "Cravings", icon: "◇", opts: ["Sweet", "Salt", "Warm", "None"], val: craving, set: setCraving },
          ].map(({ label, icon, opts, val, set }) => (
            <div key={label} style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 8 }}>
                {icon} {label}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {opts.map(o => (
                  <Pill key={o} label={o} active={val === o} onClick={() => set(o)} />
                ))}
              </div>
            </div>
          ))}

          {/* Symptoms */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 8 }}>
              + Symptoms <span style={{ fontWeight: 400, color: C.mutedLt, fontSize: 11 }}>(select any)</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {["Cramps", "Headache", "Bloating", "Backache", "Breast tenderness", "Acne", "Nausea"].map(s => (
                <Pill key={s} label={s} active={symptoms.includes(s)} onClick={() => toggleSymptom(s)} />
              ))}
            </div>
            {symptoms.includes("Cramps") && (
              <div style={{ marginTop: 12, background: C.bg, borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, color: C.dark, marginBottom: 8 }}>Cramp severity</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setCrampLevel(n)} style={{
                      width: 34, height: 34, borderRadius: "50%", cursor: "pointer",
                      border: `1.5px solid ${crampLevel >= n ? C.coral : C.border}`,
                      background: crampLevel >= n ? C.coralBg : C.white,
                      fontFamily: SANS, fontSize: 13, fontWeight: 700,
                      color: crampLevel >= n ? C.coral : C.mutedLt,
                    }}>{n}</button>
                  ))}
                  <span style={{ fontFamily: SANS, fontSize: 11, color: C.muted, marginLeft: 6 }}>
                    {crampLevel === 0 ? "Tap to rate" : crampLevel <= 2 ? "Mild" : crampLevel <= 3 ? "Moderate" : "Severe"}
                  </span>
                </div>
                {crampLevel >= 4 && (
                  <div style={{ fontFamily: SANS, fontSize: 11, color: C.coral, marginTop: 8, lineHeight: 1.5 }}>
                    Severe cramps every cycle are worth discussing with a gynaecologist — your log history can help that conversation.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 8 }}>
              ✎ Anything else?
            </div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional — a word or a paragraph, whatever helps"
              style={{
                width: "100%", minHeight: 60, padding: "12px 14px",
                border: `1.5px solid ${C.border}`, borderRadius: 10,
                fontFamily: SANS, fontSize: 13, color: C.dark, lineHeight: 1.6,
                background: C.bg, outline: "none", resize: "vertical", boxSizing: "border-box",
              }} />
          </div>

          <Btn full size="lg" onClick={handleSave} style={saved ? { background: C.green } : {}}>
            {saving ? "Saving…"
              : saved ? <><Check size={16} /> Saved — {streak}-day streak</>
              : "Save today"}
          </Btn>
          {!user && !saved && (
            <div style={{ marginTop: 10, fontFamily: SANS, fontSize: 11, color: C.mutedLt, textAlign: "center" }}>
              <button onClick={onSignIn} style={{ background: "none", border: "none", cursor: "pointer",
                fontFamily: SANS, fontSize: 11, color: C.coral, textDecoration: "underline" }}>
                Sign in
              </button> to sync your logs across devices
            </div>
          )}
          {saved && (
            <div style={{ marginTop: 12, background: C.greenBg, borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
              <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.green, marginBottom: 2 }}>
                That's {streak} days in a row. Your patterns are getting clearer.
              </div>
              <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>
                See you tomorrow — Day {currentDay + 1} of your cycle.
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* AI COACH */}
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10, background: phase.bg,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Sparkles size={16} color={phase.color} />
              </div>
              <h3 style={{ fontFamily: SERIF, fontSize: 20, color: C.dark, fontWeight: 400, margin: 0 }}>Cycle Coach</h3>
            </div>
            <div style={{
              fontFamily: SANS, fontSize: 10, letterSpacing: 1,
              color: phase.text, fontWeight: 700, textTransform: "uppercase",
              margin: "10px 0 8px",
            }}>{coachMessage.qualified}</div>
            <p style={{ fontFamily: SANS, fontSize: 13, color: C.dark, lineHeight: 1.75, margin: "0 0 16px" }}>
              {coachMessage.text}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {phase.coach.chips.map(c => (
                <span key={c} style={{
                  fontFamily: SANS, fontSize: 11, fontWeight: 600,
                  background: phase.bg, color: phase.text, padding: "6px 13px", borderRadius: 16,
                }}>{c}</span>
              ))}
            </div>
          </div>

          {/* WELLNESS PLAN */}
          <div style={card}>
            <SectionLabel>{phase.label} phase · today's plan</SectionLabel>
            <h3 style={{ fontFamily: SERIF, fontSize: 22, color: C.dark, fontWeight: 400, margin: "0 0 18px" }}>
              Your wellness plan
            </h3>
            {[
              { time: "Morning", ...phase.plan.morning },
              { time: "Afternoon", ...phase.plan.afternoon },
              { time: "Evening", ...phase.plan.evening },
              { time: "Nutrition", ...phase.plan.nutrition },
            ].map(p => (
              <div key={p.time} style={{
                display: "flex", gap: 12, alignItems: "flex-start",
                padding: "11px 0", borderBottom: `1px solid ${C.border}`,
              }}>
                <span style={{ fontSize: 17, flexShrink: 0 }}>{p.icon}</span>
                <div>
                  <div style={{
                    fontFamily: SANS, fontSize: 10, letterSpacing: 1.5,
                    color: C.muted, textTransform: "uppercase", fontWeight: 700, marginBottom: 2,
                  }}>{p.time}</div>
                  <div style={{ fontFamily: SANS, fontSize: 13, color: C.dark, lineHeight: 1.55 }}>{p.text}</div>
                </div>
              </div>
            ))}
          </div>

          {/* PATTERN INSIGHT */}
          <div style={{ ...card, background: C.coralBg, border: "none" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
              <TrendingUp size={16} color={C.coral} />
              <span style={{
                fontFamily: SANS, fontSize: 11, letterSpacing: 1.5,
                color: C.coral, fontWeight: 700, textTransform: "uppercase",
              }}>Early pattern</span>
            </div>
            <p style={{ fontFamily: SANS, fontSize: 13, color: C.dark, lineHeight: 1.7, margin: 0 }}>
              Your energy dipped 2 days before your period started this cycle.
              We'll confirm this over your next 2 cycles — if it holds, we'll nudge
              your delivery and your calendar suggestions accordingly.
            </p>
          </div>
        </div>
      </div>

      {/* HISTORY STRIP */}
      <div style={{ ...card, marginTop: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <SectionLabel>Your last 7 days</SectionLabel>
          <button onClick={() => setShowDoctor(!showDoctor)} style={{
            background: "none", border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "8px 16px", cursor: "pointer", fontFamily: SANS, fontSize: 12,
            fontWeight: 600, color: C.dark, display: "flex", alignItems: "center", gap: 7,
          }}>
            <FileText size={13} /> Share with my doctor
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 8 }}>
          <div style={{
            background: saved ? C.greenBg : C.bg, borderRadius: 10,
            padding: "12px 10px", textAlign: "center",
            border: `1.5px solid ${saved ? C.green : C.coral}`,
          }}>
            <div style={{ fontFamily: SANS, fontSize: 10, color: C.muted, marginBottom: 4 }}>Today</div>
            <div style={{ fontFamily: SERIF, fontSize: 18, color: C.dark }}>D{currentDay}</div>
            <div style={{
              fontFamily: SANS, fontSize: 9, fontWeight: 700, marginTop: 4,
              color: saved ? C.green : C.coral,
            }}>{saved ? "✓ Logged" : "Log now"}</div>
          </div>
          {(() => {
            // Real "last 6 days" from the user's logs — no fabricated data.
            const todayIso = toLocalIso(new Date());
            const recent = (logs || [])
              .filter(l => l.date && l.date !== todayIso)
              .sort((a, b) => (b.date > a.date ? 1 : -1))
              .slice(0, 6);
            if (recent.length === 0) {
              return (
                <div style={{
                  gridColumn: "span 6", background: C.bg, borderRadius: 10,
                  padding: "18px 14px", textAlign: "center",
                  fontFamily: SANS, fontSize: 12, color: C.muted,
                }}>
                  Nothing logged yet. Your last 7 days will appear here as you log each day.
                </div>
              );
            }
            return recent.map(h => {
              const day = h.day || "—";
              const summary = (h.flow && h.flow !== "None") ? h.flow
                : h.mood ? h.mood
                : h.energy ? h.energy
                : "Logged";
              return (
                <div key={h.date} style={{ background: C.bg, borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ fontFamily: SANS, fontSize: 10, color: C.muted, marginBottom: 4 }}>{formatShortDay(h.date)}</div>
                  <div style={{ fontFamily: SERIF, fontSize: 18, color: C.dark }}>D{day}</div>
                  <div style={{ fontFamily: SANS, fontSize: 9, color: C.mutedLt, marginTop: 4, lineHeight: 1.4 }}>
                    {summary}
                  </div>
                </div>
              );
            });
          })()}
        </div>
        {showDoctor && (
          <div style={{ marginTop: 16, background: C.bg, borderRadius: 12, padding: "16px 20px" }}>
            <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 6 }}>
              Doctor export includes:
            </div>
            <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, lineHeight: 1.8 }}>
              Cycle length history · Period duration · Flow pattern by day ·
              Symptom frequency (cramps, headaches, bloating) · Mood and energy trends ·
              Notes you choose to include
            </div>
            <Btn size="sm" style={{ marginTop: 12 }} onClick={async () => {
              try {
                const token = localStorage.getItem("4ever_token");
                const res = await fetch(`${API}/tracker/summary.pdf`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                  credentials: "include",
                });
                if (!res.ok) {
                  if (res.status === 401) { onSignIn && onSignIn(); return; }
                  throw new Error("failed");
                }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "4ever-cycle-summary.pdf"; a.click();
                URL.revokeObjectURL(url);
              } catch { alert("Could not generate PDF right now."); }
            }}>
              Download 3-month summary PDF <ChevronRight size={12} />
            </Btn>
            <div style={{ fontFamily: SANS, fontSize: 11, color: C.mutedLt, marginTop: 8 }}>
              Especially useful for PCOS and endometriosis consultations,
              where a clear symptom history changes the conversation.
            </div>
          </div>
        )}
      </div>

      {/* PRIVACY FOOTER */}
      <div style={{
        marginTop: 20, display: "flex", gap: 10, alignItems: "flex-start",
        padding: "16px 22px", borderRadius: 12, border: `1px solid ${C.border}`,
      }}>
        <Lock size={14} color={C.muted} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, lineHeight: 1.65 }}>
          <strong style={{ color: C.dark }}>Your data stays yours. Always.</strong>{" "}
          Everything you log is encrypted, stored on Indian servers, and never shared
          or sold. Export or permanently delete your history anytime from Settings.
        </div>
      </div>
    </div>
  );
}

// ── ROOT WRAPPER ─────────────────────────────────────────────────────────────
// Sign-in required gate — the tracker is only accessible to authenticated users.
function TrackerSignInGate({ onSignIn }) {
  return (
    <div style={{
      maxWidth: 520, margin: "60px auto", padding: "40px 32px",
      background: C.white, border: `1px solid ${C.border}`,
      borderRadius: 20, textAlign: "center",
    }} data-testid="tracker-signin-gate">
      <div style={{
        width: 56, height: 56, borderRadius: "50%", background: C.coralBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 20px",
      }}>
        <Lock size={22} color={C.coral} />
      </div>
      <h1 style={{
        fontFamily: SERIF, fontSize: 30, color: C.dark, fontWeight: 400,
        margin: "0 0 10px", lineHeight: 1.2,
      }}>
        A private space, just for you.
      </h1>
      <p style={{
        fontFamily: SANS, fontSize: 14, color: C.muted, lineHeight: 1.7,
        margin: "0 auto 26px", maxWidth: 380,
      }}>
        Your cycle is personal — so the tracker lives behind a sign-in.
        Create a free account to log how you feel, unlock the AI Cycle Coach,
        and sync deliveries to your rhythm.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
        <Btn size="lg" onClick={onSignIn} data-testid="tracker-gate-primary">
          Sign in or create account
        </Btn>
        <span style={{ fontFamily: SANS, fontSize: 12, color: C.mutedLt }}>
          Takes ~20 seconds · Encrypted · Delete anytime
        </span>
      </div>
      <div style={{
        marginTop: 28, paddingTop: 22, borderTop: `1px solid ${C.border}`,
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14,
      }}>
        {[
          ["Private", "End-to-end encrypted"],
          ["Free", "Always, no ads"],
          ["Yours", "Export or delete anytime"],
        ].map(([t, s]) => (
          <div key={t}>
            <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 700, color: C.dark }}>{t}</div>
            <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>{s}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TrackerAppPage() {
  useSEO(SEO.tracker);
  const { user, loading: authLoading, openAuth } = useAuth();
  const [onboarded, setOnboarded] = useState(false);
  const [settings, setSettings] = useState(() => ({
    lastPeriod: isoDaysAgo(7), cycleLen: 28, periodLen: 5,
  }));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load settings from backend when user changes. Signed-in users only —
  // the tracker is now gated behind auth.
  useEffect(() => {
    (async () => {
      if (user) {
        try {
          const { data } = await trackerApi.getSettings();
          if (data && data.lastPeriod) {
            setSettings({
              lastPeriod: data.lastPeriod,
              cycleLen: data.cycleLen,
              periodLen: data.periodLen,
            });
            setOnboarded(true);
          }
        } catch { /* ignore */ }
      } else {
        // Not signed in — reset state so gate shows a clean page.
        setOnboarded(false);
      }
      setLoading(false);
    })();
  }, [user]);

  const saveSettings = async (s) => {
    setSettings(s);
    if (user) {
      try { await trackerApi.putSettings(s); } catch {}
    }
  };

  // Gate: unauthenticated users see a sign-in prompt.
  if (authLoading || loading) {
    return <div style={{ minHeight: 400, display: "flex", alignItems: "center",
      justifyContent: "center", fontFamily: SERIF, fontSize: 20, color: C.muted }}>
      Loading your tracker…
    </div>;
  }
  if (!user) {
    return <TrackerSignInGate onSignIn={openAuth} />;
  }

  if (!onboarded) {
    return <Onboarding
      onComplete={async (s) => { await saveSettings(s); setOnboarded(true); }}
      onSkip={() => setOnboarded(true)}
      user={user}
      onSignIn={openAuth}
    />;
  }
  return (
    <>
      <Tracker settings={settings} onOpenSettings={() => setSettingsOpen(true)}
        user={user} onSignIn={openAuth} />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)}
        settings={settings} onSave={saveSettings} />
    </>
  );
}
